import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useHealthKit, HealthKitPreferences } from '@/hooks/useHealthKit';
import { HealthPermissionDeniedScreen } from '@/components/HealthPermissionDeniedScreen';
import { Heart, Smartphone, Check, AlertCircle, Scale, Dumbbell, Footprints, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useStepHistory } from '@/hooks/useStepHistory';
import { format } from 'date-fns';

interface HealthSettingsSectionProps {
  userId?: string;
}

/**
 * Health Settings Section - Apple Guidelines Compliant
 * 
 * Key behavior:
 * - NO custom modal before the system HealthKit permission dialog
 * - Immediately triggers iOS system dialog when user taps "Connect"
 * - Shows post-denial screen with "Open Settings" button if denied
 */
export function HealthSettingsSection({ userId }: HealthSettingsSectionProps) {
  const { 
    status, 
    loading, 
    isNativeApp,
    platform,
    platformName,
    requestAuthorization, 
    wasAuthorizationDenied,
    clearDeniedState,
    disconnect,
    readSteps,
    readWeight,
  } = useHealthKit();

  const { bulkSaveSteps, saveSteps: saveStepsToDb } = useStepHistory(userId);
  const [importing, setImporting] = useState(false);
  const [showDeniedScreen, setShowDeniedScreen] = useState(false);

  const [preferences, setPreferences] = useState<HealthKitPreferences>({
    syncEnabled: false,
    syncWeight: true,
    syncWorkouts: true,
    syncSteps: false,
  });
  const [saving, setSaving] = useState(false);

  // DO NOT auto-show denied screen on mount
  // Only show it after user clicks Connect and permission is denied
  // This allows the native prompt to re-trigger when clicking "Connect Apple Health"

  // Load preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (!userId) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('health_sync_enabled, health_sync_weight, health_sync_workouts, health_sync_steps')
        .eq('id', userId)
        .single();

      if (!error && data) {
        setPreferences({
          syncEnabled: data.health_sync_enabled ?? false,
          syncWeight: data.health_sync_weight ?? true,
          syncWorkouts: data.health_sync_workouts ?? true,
          syncSteps: data.health_sync_steps ?? false,
        });
      }
    };

    loadPreferences();
  }, [userId]);

  // Save preferences to database
  const savePreferences = async (newPrefs: HealthKitPreferences) => {
    if (!userId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          health_sync_enabled: newPrefs.syncEnabled,
          health_sync_weight: newPrefs.syncWeight,
          health_sync_workouts: newPrefs.syncWorkouts,
          health_sync_steps: newPrefs.syncSteps,
        })
        .eq('id', userId);

      if (error) throw error;
      setPreferences(newPrefs);
    } catch (error) {
      console.error('Failed to save health preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle connect button
   * 
   * iOS Behavior: Once HealthKit permission is denied, iOS will NOT show the native
   * dialog again. We detect this and immediately show the Settings redirect screen.
   * 
   * This is Apple's design - apps cannot repeatedly ask for the same permission.
   */
  const handleConnect = async () => {
    // Always clear stale denial flags before attempting connection
    // This allows re-checking if user granted permission via Settings
    clearDeniedState();
    localStorage.removeItem('health_permanently_denied');
    localStorage.removeItem('health_authorization_denied');
    setShowDeniedScreen(false);
    
    // Trigger the iOS/Android system permission dialog
    const result = await requestAuthorization();
    
    if (result.success) {
      toast.success(`Connected to ${platformName}!`);
      const newPrefs = { ...preferences, syncEnabled: true };
      savePreferences(newPrefs);
    } else if (result.wasDenied) {
      // User explicitly denied - show simple toast (no action button)
      localStorage.setItem('health_permanently_denied', 'true');
      toast('Please enable Health access in Settings', {
        duration: 5000,
      });
    } else {
      // Prompt didn't appear or was cancelled - show simple toast
      toast('Please enable Health access in Settings', {
        duration: 5000,
      });
    }
  };

  const handleDismissDeniedScreen = () => {
    setShowDeniedScreen(false);
  };

  const handleDisconnect = () => {
    disconnect();
    const newPrefs = { ...preferences, syncEnabled: false };
    savePreferences(newPrefs);
    toast.success(`Disconnected from ${platformName}`);
  };

  const handleToggle = (key: keyof HealthKitPreferences, value: boolean) => {
    const newPrefs = { ...preferences, [key]: value };
    savePreferences(newPrefs);
  };

  /**
   * Import steps from Apple Health/Health Connect and save to database
   */
  const handleImportSteps = useCallback(async () => {
    if (!status.isAuthorized) {
      toast.error('Please connect to ' + platformName + ' first');
      return;
    }

    setImporting(true);
    try {
      console.log('[HealthSync] Starting steps import...');
      
      // Read last 7 days of steps from HealthKit
      const stepsData = await readSteps(7);
      
      if (!stepsData || stepsData.length === 0) {
        toast.error('No step data available from ' + platformName);
        return;
      }

      console.log('[HealthSync] Got steps data:', stepsData);

      // Format for bulk save
      const entries = stepsData.map(entry => ({
        steps: Math.round(entry.value),
        date: format(new Date(entry.date), 'yyyy-MM-dd'),
      }));

      // Save to database
      const success = await bulkSaveSteps(entries, 'health_sync');

      if (success) {
        const totalSteps = entries.reduce((sum, e) => sum + e.steps, 0);
        toast.success(`Imported ${entries.length} days of steps (${totalSteps.toLocaleString()} total)`);
      } else {
        toast.error('Failed to save step data');
      }
    } catch (error) {
      console.error('[HealthSync] Import failed:', error);
      toast.error('Failed to import steps');
    } finally {
      setImporting(false);
    }
  }, [status.isAuthorized, platformName, readSteps, bulkSaveSteps]);

  /**
   * Import weight from Apple Health and save to database
   */
  const handleImportWeight = useCallback(async () => {
    if (!status.isAuthorized || !userId) {
      toast.error('Please connect to ' + platformName + ' first');
      return;
    }

    setImporting(true);
    try {
      console.log('[HealthSync] Starting weight import...');
      
      // Read last 30 days of weight from HealthKit
      const weightData = await readWeight(30);
      
      if (!weightData || weightData.length === 0) {
        toast.error('No weight data available from ' + platformName);
        return;
      }

      console.log('[HealthSync] Got weight data:', weightData);

      // Import each weight entry to weight_history table
      let imported = 0;
      for (const entry of weightData) {
        const { error } = await supabase
          .from('weight_history')
          .upsert({
            user_id: userId,
            weight: Math.round(entry.value * 10) / 10, // Round to 1 decimal
            logged_at: new Date(entry.date).toISOString(),
            notes: 'Imported from ' + platformName,
          }, {
            onConflict: 'user_id,logged_at',
            ignoreDuplicates: true,
          });
        
        if (!error) imported++;
      }

      if (imported > 0) {
        toast.success(`Imported ${imported} weight entries`);
      } else {
        toast.info('Weight data already up to date');
      }
    } catch (error) {
      console.error('[HealthSync] Weight import failed:', error);
      toast.error('Failed to import weight data');
    } finally {
      setImporting(false);
    }
  }, [status.isAuthorized, platformName, readWeight, userId]);

  const isApple = platform === 'apple';

  // Debug logging for native detection
  console.log('[HealthSettings] Native detection:', { isNativeApp, platform, isAuthorized: status.isAuthorized });

  // Not running in native app
  if (!isNativeApp) {
    return (
      <div className="space-y-4">
        <div className="p-6 rounded-lg border border-border/50 bg-muted/20 text-center">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium mb-2">Mobile App Required</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Health integration requires the Evolve mobile app. Sync your weight, workouts, and steps automatically.
          </p>
          <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />
              <span>Apple Health (iOS)</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-green-500" />
              <span>Health Connect (Android)</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Post-Denial Screen Modal */}
      <Dialog open={showDeniedScreen} onOpenChange={setShowDeniedScreen}>
        <DialogContent className="max-w-sm p-0">
          <HealthPermissionDeniedScreen
            platform={platform}
            onDismiss={handleDismissDeniedScreen}
          />
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        {/* Connection Status Card */}
        <div className={`p-4 rounded-lg border ${
          status.isAuthorized 
            ? 'border-green-500/30 bg-green-500/5' 
            : 'border-border/50 bg-muted/20'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              status.isAuthorized 
                ? isApple ? 'bg-red-500/20' : 'bg-green-500/20'
                : 'bg-muted/50'
            }`}>
              <Heart className={`w-6 h-6 ${
                status.isAuthorized 
                  ? isApple ? 'text-red-500' : 'text-green-500'
                  : 'text-muted-foreground'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{platformName}</p>
                {status.isAuthorized && (
                  <span className="flex items-center gap-1 text-xs text-green-500">
                    <Check className="w-3 h-3" />
                    Connected
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {status.isAuthorized 
                  ? 'Syncing your fitness data' 
                  : `Connect to sync weight, workouts & steps`}
              </p>
            </div>
          </div>

          <div className="mt-4">
            {status.isAuthorized ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                className="w-full"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={loading}
                className={`w-full ${isApple ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Heart className="w-4 h-4 mr-2" />
                )}
                Connect {platformName}
              </Button>
            )}
          </div>

          {status.lastSyncedAt && (
            <p className="text-xs text-muted-foreground text-center mt-3">
              Last synced: {new Date(status.lastSyncedAt).toLocaleString()}
            </p>
          )}
        </div>

        {/* Sync Options (only show when connected) */}
        {status.isAuthorized && (
          <div className="space-y-4 pt-4 border-t border-border/50">
            <h4 className="text-sm font-medium">Sync Settings</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Choose what data to sync with {platformName}
            </p>

            {/* Weight Sync */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Scale className="w-4 h-4 text-blue-500" />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor="sync-weight" className="text-sm">Weight</Label>
                  <p className="text-xs text-muted-foreground">
                    Evolve ↔ {platformName} (saves to both)
                  </p>
                </div>
              </div>
              <Switch
                id="sync-weight"
                checked={preferences.syncWeight}
                onCheckedChange={(v) => handleToggle('syncWeight', v)}
                disabled={saving}
              />
            </div>

            {/* Workout Sync */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Dumbbell className="w-4 h-4 text-orange-500" />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor="sync-workouts" className="text-sm">Workouts</Label>
                  <p className="text-xs text-muted-foreground">
                    Evolve → {platformName} (sends calories)
                  </p>
                </div>
              </div>
              <Switch
                id="sync-workouts"
                checked={preferences.syncWorkouts}
                onCheckedChange={(v) => handleToggle('syncWorkouts', v)}
                disabled={saving}
              />
            </div>

            {/* Steps Sync */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Footprints className="w-4 h-4 text-green-500" />
                </div>
                <div className="space-y-0.5">
                  <Label htmlFor="sync-steps" className="text-sm">Steps</Label>
                  <p className="text-xs text-muted-foreground">
                    {platformName} → Evolve (saves to history)
                  </p>
                </div>
              </div>
              <Switch
                id="sync-steps"
                checked={preferences.syncSteps}
                onCheckedChange={(v) => handleToggle('syncSteps', v)}
                disabled={saving}
              />
            </div>

            {/* Import Data Section */}
            <div className="pt-4 border-t border-border/30 space-y-3">
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Import Data
              </h5>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleImportSteps}
                  disabled={importing}
                  className="text-xs"
                >
                  {importing ? (
                    <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3 mr-1.5" />
                  )}
                  Import Steps
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleImportWeight}
                  disabled={importing}
                  className="text-xs"
                >
                  {importing ? (
                    <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3 mr-1.5" />
                  )}
                  Import Weight
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Manually import the last 7 days of steps or 30 days of weight from {platformName}
              </p>
            </div>
          </div>
        )}

        {/* Info Box - Enhanced for 5.1.3 compliance */}
        <div className="p-3 rounded-lg bg-muted/30 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {isApple 
                ? "HealthKit access can be managed in your iPhone's Settings → Privacy & Security → Health → Evolve"
                : "Health Connect access can be managed in your device's Settings → Apps → Health Connect → App Permissions"
              }
            </p>
            <p className="text-xs text-muted-foreground">
              Your health data is processed on-device and never shared for advertising or marketing.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
